import { z } from 'zod';

// Enum value arrays mirror the native pg enums in
// packages/db/src/schema/incidents.ts (incident_* enums). Keep in sync.

export const incidentSeverityValues = [
  'critical',
  'high',
  'medium',
  'low',
] as const;
export const incidentSeveritySchema = z.enum(incidentSeverityValues);
export type IncidentSeverity = z.infer<typeof incidentSeveritySchema>;

export const incidentStatusValues = [
  'open',
  'in_progress',
  'resolved',
  'closed',
  'cancelled',
] as const;
export const incidentStatusSchema = z.enum(incidentStatusValues);
export type IncidentStatus = z.infer<typeof incidentStatusSchema>;

// "Unresolved" for the header badge = open + in_progress. These are also the
// LIVE statuses that make a service read as red-flagged: while such an incident
// exists the service it is attached to — or, for an itinerary-wide incident,
// every service of that itinerary — shows the flag. Terminal statuses
// (resolved / closed / cancelled) stop flagging.
export const incidentUnresolvedStatuses = ['open', 'in_progress'] as const;

export const incidentCategoryValues = [
  'accommodation',
  'transport',
  'guide',
  'activity_experience',
  'flight',
  'restaurant',
  'booking_system',
  'payment',
  'documentation',
  'other',
] as const;
export const incidentCategorySchema = z.enum(incidentCategoryValues);
export type IncidentCategory = z.infer<typeof incidentCategorySchema>;

export const incidentResponsibilityValues = [
  'internal',
  'external_partner',
  'client',
  'shared',
  'none',
] as const;
export const incidentResponsibilitySchema = z.enum(
  incidentResponsibilityValues
);
export type IncidentResponsibility = z.infer<
  typeof incidentResponsibilitySchema
>;

export const incidentClientImpactValues = [
  'none',
  'low',
  'medium',
  'high',
  'critical',
] as const;
export const incidentClientImpactSchema = z.enum(incidentClientImpactValues);
export type IncidentClientImpact = z.infer<typeof incidentClientImpactSchema>;

export const incidentClientStatusValues = [
  'satisfied',
  'neutral',
  'dissatisfied',
  'lost',
  'pending',
] as const;
export const incidentClientStatusSchema = z.enum(incidentClientStatusValues);
export type IncidentClientStatus = z.infer<typeof incidentClientStatusSchema>;

export const incidentCompensationTypeValues = [
  'none',
  'refund',
  'voucher',
  'discount',
  'service_offered',
  'other',
] as const;
export const incidentCompensationTypeSchema = z.enum(
  incidentCompensationTypeValues
);
export type IncidentCompensationType = z.infer<
  typeof incidentCompensationTypeSchema
>;

// Which lifecycle owns the row. `red_flag` incidents are created and retired by
// the operations red-flag action (raising a flag on a service IS filing the
// report); `manual` incidents are filed through the report modal and are the
// only ones a reporter may edit freely. `import` rows were migrated from
// the pre-v3 REGISTO spreadsheet — nobody filed them here, so they are labelled
// as such rather than passing as ordinary reports.
export const incidentSourceValues = ['manual', 'red_flag', 'import'] as const;
export const incidentSourceSchema = z.enum(incidentSourceValues);
export type IncidentSource = z.infer<typeof incidentSourceSchema>;

// Screenshot / file descriptor persisted in the `attachments` jsonb column.
// Mirrors the essentials of the uploadthing AttachmentDto without coupling
// shared → uploadthing.
export const incidentAttachmentSchema = z.object({
  key: z.string(),
  url: z.string(),
  name: z.string(),
  size: z.number().optional(),
  contentType: z.string().optional(),
});
export type IncidentAttachment = z.infer<typeof incidentAttachmentSchema>;

// -------------------------------------------------------------------------
// Global reporting — generic entity reference + captured page context.
// -------------------------------------------------------------------------

// The kind of entity the reporter was looking at. Drives the Reference column
// + the "best safe URL" fallback. itinerary/planner/settings/preview all map
// to entityType 'itinerary' (the module lives in pageContext.module).
export const incidentEntityTypeValues = [
  'itinerary',
  'product',
  'operation',
  'preview',
  'other',
] as const;
export const incidentEntityTypeSchema = z.enum(incidentEntityTypeValues);
export type IncidentEntityType = z.infer<typeof incidentEntityTypeSchema>;

// Best-effort page context captured client-side at report time. ALL fields
// optional — only reliably-extractable data is attached (never invented).
export const incidentPageContextSchema = z.object({
  href: z.string().optional(),
  pathname: z.string().optional(),
  search: z.string().optional(),
  query: z.record(z.string(), z.string()).optional(),
  module: z.string().optional(),
  route: z.string().optional(),
  tab: z.string().optional(),
  title: z.string().optional(),
  entityType: z.string().optional(),
  entityId: z.string().optional(),
  itineraryId: z.string().optional(),
  productId: z.string().optional(),
  operationId: z.string().optional(),
  accountId: z.string().optional(),
  capturedAt: z.string().optional(),
  userAgent: z.string().optional(),
  language: z.string().optional(),
  viewport: z.string().optional(),
  visibleError: z.string().optional(),
  // --- Legacy REGISTO import provenance (see the import script). `z.object`
  // strips unknown keys, so these MUST be declared or the marker a re-run
  // depends on would be invisible on read.
  importSource: z.string().optional(),
  importRowKey: z.string().optional(),
  // Raw source cell values that could not be mapped onto a typed column, kept
  // so an import is lossless even when the spreadsheet held junk.
  legacyRaw: z.record(z.string(), z.string()).optional(),
});
export type IncidentPageContext = z.infer<typeof incidentPageContextSchema>;

// -------------------------------------------------------------------------
// Read shapes. Two projections so financial/management fields are never
// serialised to a plain reporter (server-side leak-proofing, not UI hiding):
//   - incidentOwnSchema  → what a reporter sees for their own incident
//   - incidentSchema     → full management view (requires incidents:read:any)
// -------------------------------------------------------------------------

export const incidentOwnSchema = z.object({
  id: z.uuid(),
  // Itinerary is OPTIONAL now — incidents are global.
  itineraryId: z.uuid().nullable(),
  itineraryItemId: z.uuid().nullable(),
  // snapshot of itinerary.itinerary_ref at create time
  itineraryRef: z.string().nullable(),
  // snapshot of the trip's country at create time (reporter-overridable)
  country: z.string().nullable(),
  // generic entity reference (itinerary | product | operation | preview | …)
  entityType: z.string().nullable(),
  entityId: z.string().nullable(),
  category: incidentCategorySchema,
  severity: incidentSeveritySchema,
  status: incidentStatusSchema,
  // 'red_flag' rows were raised from a service's red flag in Operations; the UI
  // labels them so a reporter understands why an incident they never typed into
  // the report modal is listed as theirs.
  source: incidentSourceSchema,
  whatHappened: z.string(),
  whatUserTried: z.string().nullable(),
  applicationUrl: z.string().nullable(),
  occurredOn: z.string().nullable(),
  attachments: z.array(incidentAttachmentSchema),
  // reporter sees the outcome once resolved
  resolutionNotes: z.string().nullable(),
  createdOn: z.string(),
  resolvedOn: z.string().nullable(),
});
export type IncidentOwn = z.infer<typeof incidentOwnSchema>;

export const incidentSchema = incidentOwnSchema.extend({
  organizationId: z.uuid(),
  reporterId: z.uuid(),
  reporterName: z.string().nullable(),
  // full captured page context (management inspection)
  pageContext: incidentPageContextSchema.nullable(),
  // snapshots captured at create time (audit fidelity)
  optigestNumber: z.string().nullable(),
  consultantId: z.uuid().nullable(),
  consultantName: z.string().nullable(),
  agencyOrCustomer: z.string().nullable(),
  agentName: z.string().nullable(),
  currency: z.string(),
  // triage / responsibility
  responsibility: incidentResponsibilitySchema.nullable(),
  internalPocAccountId: z.uuid().nullable(),
  internalPocName: z.string().nullable(),
  externalPoc: z.string().nullable(),
  clientImpact: incidentClientImpactSchema.nullable(),
  clientStatusAfter: incidentClientStatusSchema.nullable(),
  // compensation / recovery / cost
  compensationType: incidentCompensationTypeSchema.nullable(),
  compensationDetails: z.string().nullable(),
  amountReimbursed: z.number().nullable(),
  extraCost: z.number().nullable(),
  amountRecoveredFromPartner: z.number().nullable(),
  requestedToPartner: z.boolean(),
  // derived: reimbursed + extra - recovered (never stored)
  realCost: z.number().nullable(),
  resolvedById: z.uuid().nullable(),
  resolvedByName: z.string().nullable(),
  modifiedOn: z.string().nullable(),
  // Jira (management-only, manual). SEPARATE from `status` above.
  jiraIssueKey: z.string().nullable(),
  jiraUrl: z.string().nullable(),
  jiraStatus: z.string().nullable(),
  jiraPostedOn: z.string().nullable(),
  jiraPostedByAccountId: z.uuid().nullable(),
  jiraPostedByName: z.string().nullable(),
  jiraLastSyncedOn: z.string().nullable(),
  jiraLastSyncAttemptOn: z.string().nullable(),
  jiraLastError: z.string().nullable(),
});
export type Incident = z.infer<typeof incidentSchema>;

// -------------------------------------------------------------------------
// List inputs / results
// -------------------------------------------------------------------------

export const incidentSortFields = [
  'createdOn',
  // Last touched. NULL on an incident nobody has edited since it was filed —
  // the register shows those with no date and sorts them last either way.
  'modifiedOn',
  'occurredOn',
  'severity',
  'status',
  'category',
] as const;
export type IncidentSortField = (typeof incidentSortFields)[number];

// Reporter's own list (drawer) — minimal filters.
export const findMyIncidentsInput = z.object({
  status: z.array(incidentStatusSchema).optional(),
  page: z.number().int().min(0).default(0),
  pageSize: z.number().int().min(1).max(100).default(20),
  sortBy: z.enum(incidentSortFields).default('createdOn'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export type FindMyIncidentsInput = z.infer<typeof findMyIncidentsInput>;

// Management list (whole org) — full filter set.
export const findIncidentsInput = z.object({
  status: z.array(incidentStatusSchema).optional(),
  severity: z.array(incidentSeveritySchema).optional(),
  category: z.array(incidentCategorySchema).optional(),
  responsibility: z.array(incidentResponsibilitySchema).optional(),
  // Where the row came from — lets the register be read as "what we reported"
  // vs "what was migrated from the old spreadsheet".
  source: z.array(incidentSourceSchema).optional(),
  consultantId: z.uuid().optional(),
  reporterId: z.uuid().optional(),
  itineraryId: z.uuid().optional(),
  search: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  page: z.number().int().min(0).default(0),
  pageSize: z.number().int().min(1).max(100).default(20),
  sortBy: z.enum(incidentSortFields).default('createdOn'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export type FindIncidentsInput = z.infer<typeof findIncidentsInput>;

export const listMyIncidentsResult = z.object({
  data: z.array(incidentOwnSchema),
  count: z.number().int().nonnegative(),
});
export type ListMyIncidentsResult = z.infer<typeof listMyIncidentsResult>;

export const listIncidentsResult = z.object({
  data: z.array(incidentSchema),
  count: z.number().int().nonnegative(),
});
export type ListIncidentsResult = z.infer<typeof listIncidentsResult>;

// -------------------------------------------------------------------------
// Service red flags — the incident side of the coin, as the Operations page
// reads it. One entry per LIVE incident that makes a service read as flagged.
// -------------------------------------------------------------------------

// Why this service is flagged by this incident:
//   'service'   — the incident is attached to THIS item (a red flag raised on
//                 it, or an incident filed against it directly).
//   'itinerary' — the incident is open for the whole itinerary, so every
//                 service of that itinerary reads as flagged.
export const serviceRedFlagScopeSchema = z.enum(['service', 'itinerary']);
export type ServiceRedFlagScope = z.infer<typeof serviceRedFlagScopeSchema>;

export const serviceRedFlagSchema = z.object({
  incidentId: z.uuid(),
  scope: serviceRedFlagScopeSchema,
  source: incidentSourceSchema,
  status: incidentStatusSchema,
  severity: incidentSeveritySchema,
  category: incidentCategorySchema,
  // The incident headline — shown in the flag tooltip / notification modal.
  whatHappened: z.string(),
  createdOn: z.string(),
});
export type ServiceRedFlag = z.infer<typeof serviceRedFlagSchema>;

// -------------------------------------------------------------------------
// Mutations
// -------------------------------------------------------------------------

// Global reporting: itineraryId is now OPTIONAL. When present it is verified
// in-org + snapshotted; when absent the incident is filed against the detected
// entity (entityType/entityId) and/or the captured page context.
// reporterId, organizationId, currency and all *_snapshot values are set
// server-side; never trusted from the client.
export const createIncidentInput = z.object({
  itineraryId: z.uuid().nullable().optional(),
  itineraryItemId: z.uuid().nullable().optional(),
  entityType: incidentEntityTypeSchema.optional(),
  entityId: z.string().max(255).nullable().optional(),
  category: incidentCategorySchema,
  severity: incidentSeveritySchema,
  whatHappened: z.string().min(1),
  whatUserTried: z.string().nullable().optional(),
  // Overrides the country auto-filled from the itinerary. Omitted keeps the
  // auto-fill; an explicit null clears it.
  country: z.string().max(128).nullable().optional(),
  // full URL incl. query params (exact navigation target for the reference)
  applicationUrl: z.string().max(4096).nullable().optional(),
  pageContext: incidentPageContextSchema.optional(),
  occurredOn: z.string().nullable().optional(),
  attachments: z.array(incidentAttachmentSchema).default([]),
});
export type CreateIncidentInput = z.infer<typeof createIncidentInput>;

// Reporter edits their OWN incident while status='open' (enforced server-side).
export const updateOwnIncidentInput = z.object({
  id: z.uuid(),
  whatHappened: z.string().min(1).optional(),
  whatUserTried: z.string().nullable().optional(),
  severity: incidentSeveritySchema.optional(),
  category: incidentCategorySchema.optional(),
  attachments: z.array(incidentAttachmentSchema).optional(),
});
export type UpdateOwnIncidentInput = z.infer<typeof updateOwnIncidentInput>;

// Manager triage/resolve. status→'resolved' requires resolutionNotes to be
// present (enforced in the service against the input + existing row).
export const updateIncidentInput = z.object({
  id: z.uuid(),
  status: incidentStatusSchema.optional(),
  severity: incidentSeveritySchema.optional(),
  category: incidentCategorySchema.optional(),
  responsibility: incidentResponsibilitySchema.nullable().optional(),
  country: z.string().max(128).nullable().optional(),
  internalPocAccountId: z.uuid().nullable().optional(),
  externalPoc: z.string().nullable().optional(),
  clientImpact: incidentClientImpactSchema.nullable().optional(),
  clientStatusAfter: incidentClientStatusSchema.nullable().optional(),
  compensationType: incidentCompensationTypeSchema.nullable().optional(),
  compensationDetails: z.string().nullable().optional(),
  amountReimbursed: z.number().nullable().optional(),
  extraCost: z.number().nullable().optional(),
  amountRecoveredFromPartner: z.number().nullable().optional(),
  requestedToPartner: z.boolean().optional(),
  resolutionNotes: z.string().nullable().optional(),
});
export type UpdateIncidentInput = z.infer<typeof updateIncidentInput>;
// Jira — manual "Post to Jira" (via a confirmation modal) + status refresh from
// the management area. Op/Admin only (router update:any gate). No auto-post; the
// service rejects a second post so no duplicate Jira issue is created.
//
// The contracts themselves are generic and live in ./jira, shared with every
// other domain that posts to Jira. Re-exported here so incidents' existing
// imports keep working unchanged.
export {
  type GetJiraCreateOptionsInput,
  type GetJiraProjectMetaInput,
  getJiraCreateOptionsInput,
  getJiraProjectMetaInput,
  type JiraCreateOptions,
  type JiraIssueRef,
  type JiraIssueTypeLevel,
  type JiraProjectMeta,
  type JiraProjectOption,
  type JiraUser,
  jiraCreateOptionsSchema,
  jiraIssueRefSchema,
  jiraIssueTypeLevelSchema,
  jiraProjectMetaSchema,
  jiraProjectOptionSchema,
  jiraUserSchema,
  type PostToJiraInput,
  postToJiraInput,
  type RefreshJiraStatusInput,
  refreshJiraStatusInput,
  type SearchJiraIssuesInput,
  type SearchJiraIssuesResult,
  type SearchJiraUsersInput,
  type SearchJiraUsersResult,
  type SyncJiraAttachmentsInput,
  type SyncJiraAttachmentsResult,
  searchJiraIssuesInput,
  searchJiraIssuesResult,
  searchJiraUsersInput,
  searchJiraUsersResult,
  syncJiraAttachmentsInput,
  syncJiraAttachmentsResult,
} from './jira';

// -------------------------------------------------------------------------
// Export (exceljs backend mutation → base64; 10k row cap)
// -------------------------------------------------------------------------

export const exportIncidentsResultSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('ok'),
    filename: z.string(),
    base64: z.string(),
    rows: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal('over-cap'),
    count: z.number().int().nonnegative(),
    cap: z.number().int().positive(),
  }),
]);
export type ExportIncidentsResult = z.infer<typeof exportIncidentsResultSchema>;
